import React, { useState, useMemo, useCallback } from "react";
import {
  Box, Typography, Divider, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableHead, TableBody, TableRow,
  TableCell, Paper, CircularProgress,
} from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import { WatchActions } from "../../state";

/**
 * Super Admin → Mass Price Update. Paste (or load a CSV of) two columns —
 * model number (SKU) + new retail price — and bulk-set item_site_fields.price_retail
 * on the current site scope. price_retail syncs to TPC (payload `cost`) and the
 * update bumps date_changed, so changes are flagged for the sync. Backend enforces
 * super_admin, matches by item.model_no, previews (dry run) before applying, and
 * logs every change to Watch History. Reusable — clear the box and paste the next sheet.
 */
const SITE_NAMES = { 0: "PT", 1: "TWS", 2: "TPC" };
const siteLabel = (id) => SITE_NAMES[id] ?? `site ${id}`;
const MAX_PREVIEW_ROWS = 500;

const STATUS_STYLE = {
  update: { color: "#2e7d32", label: "Will update" },
  unchanged: { color: "#757575", label: "Unchanged" },
  unmatched: { color: "#c62828", label: "SKU not found" },
  duplicate: { color: "#e65100", label: "SKU matches >1 item" },
  invalid_price: { color: "#c62828", label: "Invalid price" },
  no_price_row: { color: "#e65100", label: "No row for this site" },
};

// Parse pasted / CSV text into { rows:[{key,price}], skipped:[{line,reason}] }.
// Accepts tab- (Excel paste) or comma- (CSV) separated. Col 1 = model_no, col 2 =
// price. Strips currency symbols/commas from the price. Skips a leading header row.
function parseText(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const rows = [];
  const skipped = [];
  lines.forEach((line, idx) => {
    let parts = line.split("\t");
    if (parts.length < 2) parts = line.split(",");
    const key = (parts[0] || "").trim();
    const rawPrice = (parts[1] ?? "").trim();
    const cleaned = rawPrice.replace(/[^0-9.]/g, "");
    const price = cleaned === "" ? NaN : parseFloat(cleaned);
    if (idx === 0 && isNaN(price)) return; // header row (e.g. "SKU, New Retail")
    if (!key || isNaN(price)) {
      skipped.push({ line, reason: !key ? "no SKU" : "no valid price" });
      return;
    }
    rows.push({ key, price: Math.round(price * 100) / 100 });
  });
  return { rows, skipped };
}

const money = (v) =>
  v === null || v === undefined || v === ""
    ? "—"
    : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function _MassPriceUpdatePage() {
  const dispatch = useDispatch();
  const siteId = useSelector((state) => state.scopes.site_id);

  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null); // { summary, preview }
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [results, setResults] = useState([]);

  const { rows, skipped } = useMemo(() => parseText(text), [text]);

  // Any edit to the box invalidates a stale preview.
  const onText = (v) => { setText(v); setPreview(null); };

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onText(String(reader.result || ""));
    reader.readAsText(file);
    e.target.value = ""; // let the same file be re-selected later
  };

  const runPreview = useCallback(async () => {
    if (!rows.length || siteId === undefined) return;
    setLoading(true);
    const data = await dispatch(
      WatchActions.massPriceUpdate(siteId, { rows, dry_run: true }),
    );
    setLoading(false);
    if (data) setPreview(data);
  }, [dispatch, siteId, rows]);

  const doApply = async () => {
    setConfirmOpen(false);
    setLoading(true);
    const data = await dispatch(
      WatchActions.massPriceUpdate(siteId, { rows, dry_run: false }),
    );
    setLoading(false);
    if (data && typeof data.updated === "number") {
      setResults((prev) => [
        {
          time: new Date().toLocaleString(),
          site: siteId,
          updated: data.updated,
          unmatched: (data.unmatched || []).length,
        },
        ...prev,
      ]);
      // Re-preview so the table reflects the now-applied state (updates → unchanged).
      runPreview();
    }
  };

  const s = preview?.summary || {};
  const willUpdate = s.update || 0;
  const problems = (s.unmatched || 0) + (s.duplicate || 0) + (s.invalid_price || 0) + (s.no_price_row || 0);
  const shown = preview ? preview.preview.slice(0, MAX_PREVIEW_ROWS) : [];

  return (
    <Box p={4} style={{ maxWidth: 1100, margin: "0 auto" }}>
      <Typography variant="h5" gutterBottom>Mass Price Update</Typography>
      <Typography variant="body2" color="textSecondary">
        Bulk-set <b>MSRP</b> from a spreadsheet (model number → new MSRP) on the current site
        scope (<b>{siteLabel(siteId)}</b>). Each item's <b>% Off is held</b> — the selling price
        (and special, if on special) moves with the MSRP. MSRP + price sync to TPC.
      </Typography>
      {siteId !== 0 && (
        <Typography variant="body2" style={{ color: "#e65100", marginTop: 6 }}>
          ⚠ You're on {siteLabel(siteId)}. TPC syncs the <b>PT</b> (site 0) retail price — switch the
          site scope to PT if you want these changes to reach TPC.
        </Typography>
      )}
      <Divider style={{ margin: "16px 0 20px" }} />

      <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>
        Paste two columns (model number&nbsp;&nbsp;·&nbsp;&nbsp;new retail price) — tab or comma
        separated — or load a CSV. A header row is ignored automatically.
      </Typography>
      <TextField
        multiline minRows={8} maxRows={16} fullWidth variant="outlined"
        placeholder={"126610LN\t14800\nIW371604\t6250\nAP15400\t21500"}
        value={text}
        onChange={(e) => onText(e.target.value)}
        InputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
      />

      <Box mt={2} display="flex" alignItems="center" style={{ gap: 16, flexWrap: "wrap" }}>
        <Button variant="outlined" component="label">
          Load CSV file…
          <input type="file" accept=".csv,text/csv,text/plain" hidden onChange={onFile} />
        </Button>
        {text && (
          <Button size="small" onClick={() => onText("")}>Clear</Button>
        )}
        <Typography variant="body2" color="textSecondary">
          {rows.length} row{rows.length === 1 ? "" : "s"} parsed
          {skipped.length ? ` · ${skipped.length} skipped` : ""}
        </Typography>
        <Box flexGrow={1} />
        <Button
          variant="contained" color="primary"
          disabled={!rows.length || loading}
          onClick={runPreview}
        >
          {loading && !confirmOpen ? <CircularProgress size={22} /> : "Preview"}
        </Button>
      </Box>

      {skipped.length > 0 && (
        <Box mt={1}>
          <Typography variant="caption" color="error">
            Skipped {skipped.length} line(s) with no SKU / no valid price
            {skipped.slice(0, 3).map((x, i) => ` · "${x.line}"`).join("")}
            {skipped.length > 3 ? " …" : ""}
          </Typography>
        </Box>
      )}

      {preview && (
        <>
          <Divider style={{ margin: "24px 0 16px" }} />
          <Box display="flex" style={{ gap: 24, flexWrap: "wrap" }} mb={2}>
            <SummaryChip label="Will update" value={willUpdate} color="#2e7d32" />
            <SummaryChip label="Unchanged" value={s.unchanged || 0} color="#757575" />
            <SummaryChip label="Not found" value={s.unmatched || 0} color="#c62828" />
            <SummaryChip label="Duplicate SKU" value={s.duplicate || 0} color="#e65100" />
            {(s.invalid_price || s.no_price_row) ? (
              <SummaryChip label="Other issues" value={(s.invalid_price || 0) + (s.no_price_row || 0)} color="#e65100" />
            ) : null}
          </Box>

          <Button
            variant="contained" color="primary"
            disabled={loading || willUpdate === 0}
            onClick={() => setConfirmOpen(true)}
          >
            {loading && confirmOpen ? <CircularProgress size={22} /> : `Apply ${willUpdate} price change${willUpdate === 1 ? "" : "s"}`}
          </Button>

          <Box mt={2} component={Paper} variant="outlined" style={{ maxHeight: 420, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Model No.</TableCell>
                  <TableCell>Item ID</TableCell>
                  <TableCell align="right">MSRP</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">% Off</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((p, i) => {
                  const st = STATUS_STYLE[p.status] || { color: "#000", label: p.status };
                  return (
                    <TableRow key={i}>
                      <TableCell style={{ fontFamily: "monospace" }}>{p.key}</TableCell>
                      <TableCell>{p.item_id ?? (p.item_ids ? p.item_ids.join(", ") : "—")}</TableCell>
                      <TableCell align="right"><FromTo from={p.retail_from} to={p.retail_to} /></TableCell>
                      <TableCell align="right"><FromTo from={p.our_from} to={p.our_to} /></TableCell>
                      <TableCell align="right">{p.pct_off === null || p.pct_off === undefined ? "—" : `${p.pct_off}%`}</TableCell>
                      <TableCell style={{ color: st.color, fontWeight: 600 }}>{st.label}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {preview.preview.length > MAX_PREVIEW_ROWS && (
              <Box p={1}>
                <Typography variant="caption" color="textSecondary">
                  Showing first {MAX_PREVIEW_ROWS} of {preview.preview.length} rows (counts above cover all).
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm price update</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Update MSRP on <b>{willUpdate}</b> item(s) on <b>{siteLabel(siteId)}</b>, holding each
            item's % Off (the selling price moves with the MSRP).
            {problems > 0 && ` ${problems} row(s) will be skipped (not found / duplicate / invalid).`}
          </Typography>
          <Typography variant="body2" color="error" style={{ marginTop: 12 }}>
            This cannot be undone (changes are logged to Watch History). Proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={doApply}>Yes, update prices</Button>
        </DialogActions>
      </Dialog>

      {results.length > 0 && (
        <Box mt={4} component={Paper} variant="outlined" p={2}>
          <Typography variant="h6" gutterBottom>Results</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Site</TableCell>
                <TableCell>Prices updated</TableCell>
                <TableCell>Not found</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.time}</TableCell>
                  <TableCell>{siteLabel(r.site)}</TableCell>
                  <TableCell>{r.updated}</TableCell>
                  <TableCell>{r.unmatched}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

// Renders "old → new" for a price cell: strikethrough old + bold new when it moves,
// a single muted value when unchanged, or just the target for unmatched/etc. rows.
function FromTo({ from, to }) {
  const hasFrom = from !== undefined && from !== null;
  const hasTo = to !== undefined && to !== null;
  if (!hasFrom && !hasTo) return "—";
  if (!hasFrom) return money(to);
  if (from === to) return <span style={{ color: "#757575" }}>{money(from)}</span>;
  return (
    <span>
      <span style={{ color: "#9e9e9e", textDecoration: "line-through" }}>{money(from)}</span>
      {" → "}
      <b>{money(hasTo ? to : from)}</b>
    </span>
  );
}

function SummaryChip({ label, value, color }) {
  return (
    <Box display="flex" flexDirection="column">
      <Typography variant="h5" style={{ color, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography variant="caption" color="textSecondary">{label}</Typography>
    </Box>
  );
}

export const MassPriceUpdatePage = React.memo(_MassPriceUpdatePage);
export default MassPriceUpdatePage;
